/* ==========================================================================
   Nooroots | Ashwagandha & Lion's Mane Formula Comparison
   CALCULATION ENGINE (build stage 3)

   Pure calculation module. It never touches the page (no DOM), never changes
   the dataset, and gives the same answer for the same inputs.

   Works in the browser (window.AshlionComparisonEngine) and in Node
   (require) so the numerical fixtures can run in a terminal.

   Specification: Task 11 V2 (version 1.1), sections 4-13.
   ========================================================================== */
(function (root, factory) {
  'use strict';
  var engine = factory();
  if (typeof module === 'object' && module.exports) module.exports = engine;
  else root.AshlionComparisonEngine = engine;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var ENGINE_VERSION = '1.0.1';
  var SUPPORTED_SCHEMA_MAJOR = 1;
  var INGREDIENTS = ['ashwagandha', 'lions_mane'];
  var MARKERS = {
    withanolides: { ingredient: 'ashwagandha', k: 1, label: 'withanolides' },
    polysaccharides: { ingredient: 'lions_mane', k: 100, label: 'polysaccharides' },
    beta_glucans: { ingredient: 'lions_mane', k: 100, label: 'beta-glucans' }
  };
  var INGREDIENT_LABEL = { ashwagandha: 'Ashwagandha', lions_mane: 'Lion’s mane' };
  var EXTRACT_K = 100;

  /* ------------------------------------------------------------------------
     1. Numbers, tolerance and units
     ------------------------------------------------------------------------ */

  // Section 8: differences within 1e-9 x max(1,|x|,|y|) are numerical noise.
  function near(x, y) { return Math.abs(x - y) <= 1e-9 * Math.max(1, Math.abs(x), Math.abs(y)); }
  function lt(x, y) { return x < y && !near(x, y); }
  function gt(x, y) { return x > y && !near(x, y); }
  function isNum(v) { return typeof v === 'number' && isFinite(v); }

  var MASS_FACTORS = { mg: 1, g: 1000, mcg: 0.001, 'µg': 0.001, ug: 0.001, microgram: 0.001, micrograms: 0.001 };

  function toMg(value, unit) {
    if (!isNum(value)) return null;
    var f = MASS_FACTORS[String(unit || 'mg').toLowerCase()];
    return f === undefined ? null : value * f;
  }

  /**
   * Parse text copied from a label: "£19.99", "1,000", "300 mg", "0.5 g".
   * Returns { ok, value, unit, decimals, empty, error }.
   * Ambiguous text such as "1,5" is rejected rather than read as 15.
   */
  function parseNumber(text) {
    if (text === null || text === undefined) return { ok: false, empty: true };
    if (typeof text === 'number') {
      return isNum(text) ? { ok: true, value: text, unit: null, decimals: decimalsOf(String(text)) }
        : { ok: false, error: 'NOT_FINITE' };
    }
    var s = String(text).trim();
    if (s === '') return { ok: false, empty: true };
    s = s.replace(/^£\s*/, '').replace(/\s*(gbp)$/i, '');
    var m = s.match(/^([+-]?[0-9][0-9,]*(?:\.[0-9]+)?|[+-]?\.[0-9]+)\s*(mg|g|mcg|µg|ug|%)?$/i);
    if (!m) return { ok: false, error: 'UNREADABLE_NUMBER' };
    var num = m[1];
    if (num.indexOf(',') !== -1) {
      // Thousands separators only: 1,000 or 12,500.50. Anything else is ambiguous.
      if (!/^[+-]?[0-9]{1,3}(,[0-9]{3})+(\.[0-9]+)?$/.test(num)) return { ok: false, error: 'AMBIGUOUS_SEPARATOR' };
      num = num.replace(/,/g, '');
    }
    var value = Number(num);
    if (!isNum(value)) return { ok: false, error: 'NOT_FINITE' };
    return { ok: true, value: value, unit: m[2] ? m[2].toLowerCase() : null, decimals: decimalsOf(num) };
  }

  function decimalsOf(numText) {
    var i = String(numText).indexOf('.');
    return i === -1 ? 0 : String(numText).length - i - 1;
  }

  /* ------------------------------------------------------------------------
     2. Statistics and positions (sections 7 and 8)
     ------------------------------------------------------------------------ */

  // R7 inclusive quantile on a sorted ascending array.
  function quantileR7(sorted, p) {
    var n = sorted.length;
    if (n === 0) return null;
    if (n === 1 || p <= 0) return sorted[0];
    if (p >= 1) return sorted[n - 1];
    var h = 1 + (n - 1) * p;
    var j = Math.floor(h);
    var f = h - j;
    if (j >= n) return sorted[n - 1];
    return sorted[j - 1] + f * (sorted[j] - sorted[j - 1]);
  }

  function summarise(values) {
    var y = values.slice().sort(function (a, b) { return a - b; });
    var n = y.length;
    if (n === 0) return { exact_n: 0, median: null, p25: null, p75: null, min: null, max: null, mean: null };
    var sum = 0;
    for (var i = 0; i < n; i++) sum += y[i];
    return {
      exact_n: n, median: quantileR7(y, 0.5), p25: quantileR7(y, 0.25), p75: quantileR7(y, 0.75),
      min: y[0], max: y[n - 1], mean: sum / n
    };
  }

  /**
   * Exact (fixed-only) position. direction 'amount': favourable = peer < target.
   * direction 'cost': favourable (less expensive) = peer > target. Ties counted separately.
   */
  function exactPosition(target, peers, direction) {
    var strict = 0, ties = 0;
    for (var i = 0; i < peers.length; i++) {
      if (near(peers[i], target)) ties++;
      else if (direction === 'cost' ? peers[i] > target : peers[i] < target) strict++;
    }
    var n = peers.length;
    return { n: n, strict_count: strict, tie_count: ties, strict_percent: n ? 100 * strict / n : null };
  }

  /**
   * Interval position. Target [a,b], peers [l,u]. Section 7.
   * amount: L = count(u < a), H = count(l < b)
   * cost:   L = count(l > b), H = count(u > a)
   */
  function intervalPosition(a, b, peers, direction) {
    var L = 0, H = 0;
    for (var i = 0; i < peers.length; i++) {
      var l = peers[i][0], u = peers[i][1];
      if (direction === 'cost') { if (gt(l, b)) L++; if (gt(u, a)) H++; }
      else { if (lt(u, a)) L++; if (lt(l, b)) H++; }
    }
    var n = peers.length;
    return {
      n: n, definite_count: L, possible_count: H,
      percent_bounds: n ? [100 * L / n, 100 * H / n] : null,
      // A single count is a whole-percent claim (round down); a real interval rounds outward.
      display_bounds: n ? (L === H ? [Math.floor(100 * L / n + 1e-9), Math.floor(100 * L / n + 1e-9)]
        : [Math.floor(100 * L / n + 1e-9), Math.ceil(100 * H / n - 1e-9)]) : null
    };
  }

  // Optional fixed-reference bands; ties at a cutoff stay Middle.
  function bandFor(value, s, direction) {
    if (!isNum(value) || s.p25 === null) return null;
    var lowName = direction === 'cost' ? 'less_expensive' : 'lower';
    var highName = direction === 'cost' ? 'more_expensive' : 'higher';
    if (lt(value, s.p25)) return lowName;
    if (gt(value, s.p75)) return highName;
    return 'middle';
  }

  function bandSpan(min, max, s, direction) {
    var a = bandFor(min, s, direction), b = bandFor(max, s, direction);
    if (a === null || b === null) return null;
    return a === b ? [a] : [a, b];
  }

  // Section 7 presentation policy by eligible peer count.
  function benchmarkStateFor(n) {
    if (n >= 10) return 'available';
    if (n >= 5) return 'small_subset';
    if (n >= 1) return 'insufficient_peers';
    return 'no_peers';
  }

  /* ------------------------------------------------------------------------
     3. Normalized label model
     Both catalogue records and manual entries become this shape, so every
     product goes through exactly the same calculation path.
     ------------------------------------------------------------------------ */

  function blankRoutine(state) {
    return { state: state || 'not_found', qualifier: 'unresolved', min: null, max: null, unit: null, eligible: false, scope: 'shared', reason_codes: [] };
  }

  function routineFromRecord(r) {
    if (!r) return blankRoutine();
    return {
      state: r.value_state, qualifier: r.qualifier, min: r.quantity_min, max: r.quantity_max,
      unit: r.unit, eligible: !!r.calculation_eligible, scope: r.scope || 'shared',
      instructions: r.instructions || null, reason_codes: (r.reason_codes || []).slice()
    };
  }

  function basisFromRecord(b) {
    if (!b) return { kind: 'unknown', quantity: null, unit: null, eligible: false, pack_conversion: false, reason_codes: [] };
    return {
      kind: b.kind === 'per_day' ? 'per_day' : (b.value_state === 'known' ? 'physical_units' : 'unknown'),
      quantity: b.quantity, unit: b.unit, eligible: !!b.calculation_eligible,
      pack_conversion: !!b.pack_conversion_eligible, reason_codes: (b.reason_codes || []).slice()
    };
  }

  /** Convert one released dataset record into the normalized label model. */
  function targetFromFormula(rec) {
    var t = {
      origin: 'catalogue',
      source_formula_id: rec.formula_id,
      formula_version: rec.formula_version,
      edited: false,
      display_name: rec.brand + ' – ' + rec.name,
      reviewed_at: rec.reviewed_at || null,
      scope: {
        status: rec.status,
        ashwagandha_present: rec.ingredient_presence ? rec.ingredient_presence.ashwagandha : null,
        lions_mane_present: rec.ingredient_presence ? rec.ingredient_presence.lions_mane : null,
        age_population: rec.age_population,
        adult_comparator: !!rec.adult_comparator,
        purpose_eligible: rec.status === 'active'
      },
      routine: routineFromRecord(rec.routine),
      ingredients: {},
      offer: null,
      coverage: rec.coverage || null
    };
    (rec.ingredients || []).forEach(function (ing) {
      var basis = basisFromRecord(ing.basis);
      var I = {
        basis: basis,
        routine: routineFromRecord(ing.routine),
        measurements: (ing.measurements || []).map(function (m) {
          return {
            id: m.measurement_id, preparation: m.preparation, amount_kind: m.amount_kind,
            min: m.amount_min_mg, max: m.amount_max_mg, qualifier: m.qualifier, state: m.value_state,
            eligible: !!m.calculation_eligible, extract_eligible: !!m.extract_comparison_eligible,
            basis: basisFromRecord(m.basis), origin: m.origin, quantity_status: m.quantity_status,
            reason_codes: (m.reason_codes || []).slice()
          };
        }),
        markers: (ing.markers || []).map(function (k) {
          var reported = k.origin === 'reported' && isNum(k.amount_min_mg)
            ? { min: k.amount_min_mg, max: k.amount_max_mg, qualifier: k.qualifier } : null;
          var pct = k.percentage_value_state === 'known' && isNum(k.percentage_min)
            ? { min: k.percentage_min, max: k.percentage_max, qualifier: k.percentage_qualifier } : null;
          return {
            name: k.name, linked_measurement_id: k.linked_measurement_id, reported: reported, percentage: pct,
            basis: basisFromRecord(k.basis), state: k.value_state, eligible: !!k.calculation_eligible,
            origin: k.origin, stored_min: k.amount_min_mg, stored_max: k.amount_max_mg,
            assay_known: !!(k.assay && k.assay.value_state === 'known'),
            reason_codes: (k.reason_codes || []).slice()
          };
        }),
        headline: ing.unresolved_headline_amount && ing.unresolved_headline_amount.value_state === 'known'
          ? { text: String(ing.unresolved_headline_amount.source_text) } : null,
        der: null // Dataset ratios are descriptive only (equivalent_context.use)
      };
      t.ingredients[ing.ingredient] = I;
    });
    var offer = (rec.offers || []).filter(function (o) { return o.price_class === 'recorded_normal_list'; })[0];
    if (offer) {
      t.offer = {
        total_gbp: offer.total_gbp, pack_quantity: offer.pack_quantity, pack_unit: offer.pack_unit,
        price_class: 'recorded_normal_list', orderability: offer.orderability,
        state: offer.price_value_state === 'known' && offer.pack_value_state === 'known' ? 'known' : 'not_found',
        pool_eligible: !!offer.recorded_baseline_eligible, observed_at: offer.observed_at,
        reason_codes: (offer.reason_codes || []).slice()
      };
    }
    return t;
  }

  /* ------------------------------------------------------------------------
     4. Manual entry -> normalized label model, with validation (section 4)
     Field names match the HTML form (name="..."). Pure: takes a plain object.
     ------------------------------------------------------------------------ */

  var PREFIX = { ashwagandha: 'ash', lions_mane: 'lm' };

  function buildManualTarget(values, options) {
    values = values || {};
    options = options || {};
    var errors = [];
    var notes = [];

    function v(name) { var x = values[name]; return x === undefined || x === null ? '' : String(x).trim(); }

    // Numeric field with validation. Returns number or null. kind: amount|positive|percent
    function num(name, kind, label, unitField) {
      var p = parseNumber(values[name]);
      if (p.empty) return null;
      if (!p.ok) { errors.push({ field: name, code: 'INPUT_INVALID', message: 'Enter ' + label + ' as a number, for example 300' }); return null; }
      var val = p.value;
      if (p.unit === '%' && kind !== 'percent') { errors.push({ field: name, code: 'INPUT_INVALID', message: unitField !== undefined ? 'This box takes mg, not a percentage' : 'Enter a number without a % sign' }); return null; }
      if (p.unit && p.unit !== '%' && unitField !== undefined) {
        var mg = toMg(val, p.unit);
        if (mg !== null) { notes.push({ field: name, code: 'UNIT_INTERPRETED', unit: p.unit }); return checkRange(mg); }
      }
      return checkRange(val);
      function checkRange(x) {
        if (x < 0) { errors.push({ field: name, code: 'INPUT_INVALID', message: 'Enter ' + label + ' of 0 or more' }); return null; }
        if (kind === 'positive' && x === 0) { errors.push({ field: name, code: 'INPUT_INVALID', message: 'Enter ' + label + ' greater than 0' }); return null; }
        if (kind === 'percent' && x > 100) { errors.push({ field: name, code: 'INPUT_INVALID', message: 'Enter a percentage of 100 or less' }); return null; }
        return x;
      }
    }

    function presence(x) { return x === 'yes' ? true : x === 'no' ? false : null; }

    var ageMap = { adult: 'adult_general', children: 'children', mixed: 'mixed_unresolved' };
    var t = {
      origin: 'user_entered',
      source_formula_id: options.source_formula_id || null,
      edited: !!options.source_formula_id,
      display_name: v('product_name') || null,
      scope: {
        status: 'active',
        ashwagandha_present: presence(v('ash_presence')),
        lions_mane_present: presence(v('lm_presence')),
        age_population: ageMap[v('age_population')] || null,
        purpose_eligible: v('product_purpose') === 'eligible_supplement' ? true
          : (['excluded_primary', 'beverage', 'bundle'].indexOf(v('product_purpose')) !== -1 ? false : null)
      },
      routine: blankRoutine(),
      ingredients: {},
      offer: null,
      reason_codes: []
    };
    t.scope.adult_comparator = t.scope.age_population === 'adult_general';

    // Conversion for scoops / pipettes (only when the label states it).
    var conversion = null;
    var convValue = num('conversion_value', 'positive', 'the conversion');
    if (convValue !== null && v('conversion_unit')) conversion = { factor: convValue, unit: v('conversion_unit') };

    // Shared routine
    var rs = v('routine_status');
    if (rs === 'fixed' || rs === 'finite_range') {
      var dmin = num('routine_min', 'amount', 'a daily quantity');
      var dmax = rs === 'finite_range' ? num('routine_max', 'amount', 'an upper daily quantity') : dmin;
      if (rs === 'fixed' && v('routine_max') !== '') dmax = num('routine_max', 'amount', 'an upper daily quantity');
      if (dmin !== null && dmax !== null && gt(dmin, dmax)) {
        errors.push({ field: 'routine_max', code: 'INPUT_INVALID', message: 'The upper daily quantity must be at least the lower one' });
      } else if (dmin !== null && dmax !== null && v('routine_unit')) {
        t.routine = {
          state: 'known', qualifier: near(dmin, dmax) ? 'exact' : 'finite_range', min: dmin, max: dmax,
          unit: v('routine_unit'), eligible: true, scope: 'shared', reason_codes: []
        };
      } else if (dmin !== null) {
        t.routine = blankRoutine('unclear');
        t.routine.reason_codes.push(v('routine_unit') ? 'DAILY_FREQUENCY_UNKNOWN' : 'UNIT_UNMATCHED');
      }
    } else if (rs === 'unclear') {
      t.routine = blankRoutine('unclear');
      t.routine.reason_codes.push('DAILY_FREQUENCY_UNKNOWN');
    }
    t.routine = applyConversion(t.routine, conversion);

    // Shared basis
    var sharedBasis = { kind: 'unknown', quantity: null, unit: null, eligible: false, pack_conversion: false, reason_codes: ['BASIS_MISSING'] };
    var bt = v('basis_type');
    if (bt === 'single_unit') sharedBasis = physicalBasis(1, v('basis_unit'));
    else if (bt === 'physical_serving') sharedBasis = physicalBasis(num('basis_quantity', 'positive', 'the quantity in the label basis'), v('basis_unit'));
    else if (bt === 'daily') sharedBasis = { kind: 'per_day', quantity: null, unit: null, eligible: true, pack_conversion: false, reason_codes: [] };

    function physicalBasis(q, unit) {
      if (q === null || !unit) {
        return { kind: 'unknown', quantity: q, unit: unit || null, eligible: false, pack_conversion: false, reason_codes: ['BASIS_MISSING'] };
      }
      return applyConversion({ kind: 'physical_units', quantity: q, unit: unit, eligible: true, pack_conversion: true, reason_codes: [] }, conversion);
    }

    INGREDIENTS.forEach(function (ing) {
      var px = PREFIX[ing];
      var I = { basis: sharedBasis, routine: null, measurements: [], markers: [], headline: null, der: null };
      var obt = v(px + '_basis_type');
      if (obt === 'physical') I.basis = physicalBasis(num(px + '_basis_quantity', 'positive', 'the quantity in that basis'), v(px + '_basis_unit'));
      else if (obt === 'daily') I.basis = { kind: 'per_day', quantity: null, unit: null, eligible: true, pack_conversion: false, reason_codes: [] };
      else if (obt === 'unknown') I.basis = { kind: 'unknown', quantity: null, unit: null, eligible: false, pack_conversion: false, reason_codes: ['BASIS_MISSING'] };

      var unit = v(px + '_amount_unit') || 'mg';
      var amtText = v(px + '_amount');
      var amtParsed = parseNumber(values[px + '_amount']);
      var unitOverride = amtParsed.ok && amtParsed.unit && amtParsed.unit !== '%' ? amtParsed.unit : null;
      var maxParsed = parseNumber(values[px + '_amount_max']);
      var maxUnit = maxParsed.ok && maxParsed.unit && maxParsed.unit !== '%' ? maxParsed.unit : null;
      var amin = num(px + '_amount', 'amount', 'an amount');
      var amax = num(px + '_amount_max', 'amount', 'an upper amount');
      if (amin !== null) amin = toMg(amin, unitOverride || unit);
      if (amax !== null) amax = toMg(amax, maxUnit || unitOverride || unit);
      var qual = v(px + '_qualifier') || 'unresolved';
      if (qual === 'unresolved') qual = amax !== null && !near(amax, amin) ? 'finite_range' : 'exact';
      if (qual === 'finite_range' && amin !== null && amax === null) {
        errors.push({ field: px + '_amount_max', code: 'INPUT_INVALID', message: 'Enter the upper amount of the range' });
      }
      if (qual === 'finite_range' && amin !== null && amax !== null && gt(amin, amax)) {
        errors.push({ field: px + '_amount_max', code: 'INPUT_INVALID', message: 'The upper amount must be at least the lower amount' });
        amin = null;
      }
      var mmin = amin, mmax = amin;
      if (qual === 'finite_range') mmax = amax;
      if (qual === 'minimum') mmax = null;
      if (qual === 'maximum') { mmin = null; mmax = amin; }

      var kind = v(px + '_amount_kind') || 'unknown';
      var prep = v(px + '_preparation') || 'unknown';
      var mainId = px + '_amount';

      if (amin !== null) {
        if (kind === 'actual_preparation') {
          var p2 = { extract: 'extract', powder: 'powder', biomass: 'biomass' }[prep];
          if (p2) {
            I.measurements.push(manualMeasurement(mainId, p2, mmin, mmax, qual));
          } else if (prep === 'extract_biomass' || prep === 'mixed') {
            // A combined total is never split between preparations.
            I.measurements.push(manualMeasurement(mainId, 'unspecified', mmin, mmax, qual, ['PREPARATION_COMBINED']));
          } else {
            I.measurements.push(manualMeasurement(mainId, 'unspecified', mmin, mmax, qual, ['PREPARATION_UNCLEAR']));
          }
        } else {
          // Starting-material equivalent, blend total or unclear meaning: quoted only.
          I.headline = {
            text: amtText + (unitOverride ? '' : ' ' + unit),
            reason: kind === 'raw_equivalent' ? 'EQUIVALENT_NOT_REVERSED' : kind === 'blend_total' ? 'BLEND_NOT_ALLOCATED' : 'AMOUNT_MEANING_UNCLEAR'
          };
        }
      }
      // Separately printed split amounts (mg)
      var splitA = ing === 'ashwagandha' ? [['ash_split_extract_mg', 'extract'], ['ash_split_powder_mg', 'powder']]
        : [['lm_split_extract_mg', 'extract'], ['lm_split_biomass_mg', 'biomass']];
      splitA.forEach(function (s) {
        var x = num(s[0], 'amount', 'a separate amount', 'mg');
        if (x !== null) I.measurements.push(manualMeasurement(s[0].replace(/_mg$/, ''), s[1], x, x, 'exact'));
      });
      // A total and a separate part of the same preparation would be counted twice: keep the parts.
      var seenPrep = {};
      var duplicate = I.measurements.some(function (m) {
        if (m.preparation !== 'unspecified' && seenPrep[m.preparation]) return true;
        seenPrep[m.preparation] = true;
        return false;
      });
      if (duplicate) {
        errors.push({ field: mainId, code: 'INPUT_INVALID', message: 'Enter either the total amount or the separate parts, not both' });
        I.measurements = I.measurements.filter(function (m) { return m.id !== mainId; });
      }

      // Markers for this ingredient
      Object.keys(MARKERS).forEach(function (name) {
        if (MARKERS[name].ingredient !== ing) return;
        var rep = num(name + '_reported_mg', 'amount', 'a marker amount', 'mg');
        var repMax = num(name + '_reported_mg_max', 'amount', 'an upper marker amount', 'mg');
        var pc = num(name + '_percent', 'percent', 'a percentage');
        var pcMax = num(name + '_percent_max', 'percent', 'an upper percentage');
        if (rep === null && pc === null) {
          // Missing is not zero: record why the marker result is unavailable.
          if (I.measurements.length) I.markers.push({ name: name, state: 'not_provided', eligible: false, reason_codes: ['MARKER_NOT_PROVIDED'] });
          return;
        }
        var mq = v(name + '_qualifier') || 'unresolved';
        if (mq === 'unresolved') mq = (repMax !== null || pcMax !== null) ? 'finite_range' : 'exact';
        function bounds(lo, hi) {
          if (lo === null) return null;
          if (mq === 'minimum') return { min: lo, max: null, qualifier: 'minimum' };
          if (mq === 'maximum') return { min: null, max: lo, qualifier: 'maximum' };
          if (hi !== null && gt(lo, hi)) {
            errors.push({ field: name + '_percent_max', code: 'INPUT_INVALID', message: 'The upper value must be at least the lower value' });
            return null;
          }
          return { min: lo, max: hi === null ? lo : hi, qualifier: hi === null || near(lo, hi) ? 'exact' : 'finite_range' };
        }
        var link = v(name + '_linked_measurement');
        var linkedId = link && link !== 'unknown' ? link.replace(/_mg$/, '') : null;
        var mb = v(name + '_basis');
        var markerBasis = mb === 'daily' ? { kind: 'per_day', quantity: null, unit: null, eligible: true, pack_conversion: false, reason_codes: [] }
          : mb === 'ingredient' ? null : (rep !== null && mb !== 'ingredient' ? { kind: 'unknown', reason_codes: ['BASIS_MISSING'] } : null);
        var repDecimals = parseNumber(values[name + '_reported_mg']).decimals;
        I.markers.push({
          name: name, linked_measurement_id: linkedId, reported: bounds(rep, repMax), percentage: bounds(pc, pcMax),
          reported_decimals: repDecimals, basis: markerBasis, state: 'known', eligible: true,
          origin: rep !== null ? 'reported' : 'calculated', reason_codes: []
        });
      });
      t.ingredients[ing] = I;
    });

    // Offer
    var price = num('price_gbp', 'amount', 'a price');
    var packQ = num('pack_quantity', 'positive', 'the pack quantity');
    var delivery = num('delivery_gbp', 'amount', 'a delivery charge');
    if (price !== null || packQ !== null) {
      var pcMap = { one_time: 'one_time', recorded_baseline: 'recorded_normal_list', promotion: 'promotion', subscription: 'subscription', reference: 'reference' };
      t.offer = {
        total_gbp: price, pack_quantity: packQ, pack_unit: v('pack_unit') || null,
        price_class: pcMap[v('price_class')] || 'unknown',
        orderability: v('offer_orderability') === 'unavailable' ? 'unavailable' : v('offer_orderability') === 'available' ? 'orderable_listing' : 'unknown',
        delivery_gbp: delivery, state: price !== null && packQ !== null && v('pack_unit') ? 'known' : 'incomplete',
        pool_eligible: false, reason_codes: []
      };
      if (t.offer.pack_unit === null && packQ !== null) t.offer.reason_codes.push('UNIT_UNMATCHED');
    }

    function manualMeasurement(id, prep, min, max, qual, codes) {
      return {
        id: id, preparation: prep, amount_kind: 'actual_preparation', min: min, max: max, qualifier: qual,
        state: 'known', eligible: true, extract_eligible: prep === 'extract', basis: null, origin: 'user_entered',
        reason_codes: codes || []
      };
    }

    return { target: t, errors: errors, notes: notes };
  }

  // Express scoop/pipette quantities in g/mL when the label states the conversion.
  function applyConversion(obj, conversion) {
    if (!conversion || !obj || !obj.unit) return obj;
    var from = conversion.unit === 'g' ? 'scoop' : 'pipette';
    if (obj.unit !== from) return obj;
    var out = {};
    Object.keys(obj).forEach(function (k) { out[k] = obj[k]; });
    out.unit = conversion.unit;
    ['quantity', 'min', 'max'].forEach(function (k) { if (isNum(out[k])) out[k] = out[k] * conversion.factor; });
    out.converted_from = from;
    return out;
  }

  /* ------------------------------------------------------------------------
     5. Local calculations (sections 5 and 6)
     Each result is evaluated on its own dependencies only.
     ------------------------------------------------------------------------ */

  function result(metricId, props) {
    var r = {
      metric_id: metricId, calculation_state: 'unavailable', value: null, reason_codes: [],
      provenance: null, pool_eligible: false, range_origin: null
    };
    for (var k in props) if (Object.prototype.hasOwnProperty.call(props, k)) r[k] = props[k];
    return r;
  }

  function stateOf(min, max, qualifier) {
    if (qualifier === 'minimum' || (isNum(min) && max === null)) return isNum(min) ? 'minimum_only' : 'unavailable';
    if (qualifier === 'maximum' || (min === null && isNum(max))) return isNum(max) ? 'maximum_only' : 'unavailable';
    if (!isNum(min) || !isNum(max)) return 'unavailable';
    return near(min, max) ? 'exact' : 'bounded';
  }

  function value(min, max, unit, extra) {
    var v = { min: min, max: max, point: isNum(min) && isNum(max) && near(min, max) ? min : null, unit: unit };
    if (extra) for (var k in extra) v[k] = extra[k];
    return v;
  }

  function routineFor(target, I) {
    if (I && I.routine && I.routine.eligible) return I.routine;
    if (I && I.routine && I.routine.scope === 'ingredient_specific') return I.routine;
    return target.routine;
  }

  function basisFor(I, m) { return (m && m.basis && m.basis.kind !== 'unknown') ? m.basis : (m && m.basis) || I.basis; }

  /**
   * Daily amount from an amount per basis. q in [qmin,qmax] per b units, d in [dmin,dmax] units/day.
   * Daily = q x d / b (section 6). Per-day basis: daily = q, no extra multiplier.
   */
  function dailyAmount(qmin, qmax, qual, basis, routine) {
    if (!basis || basis.kind === 'unknown' || !basis.eligible) return { state: 'unavailable', reason_codes: ['BASIS_MISSING'] };
    if (basis.kind === 'per_day') {
      return { state: stateOf(qmin, qmax, qual), min: qmin, max: qmax, range_origin: qual === 'finite_range' ? 'declared_quantity' : null, reason_codes: [] };
    }
    if (!routine || !routine.eligible || routine.state !== 'known') {
      return { state: 'unavailable', reason_codes: ['DAILY_FREQUENCY_UNKNOWN'] };
    }
    if (routine.unit !== basis.unit) return { state: 'unavailable', reason_codes: ['UNIT_UNMATCHED'] };
    if (!isNum(basis.quantity) || basis.quantity <= 0) return { state: 'unavailable', reason_codes: ['BASIS_MISSING'] };
    var b = basis.quantity;
    var lo = isNum(qmin) ? qmin * routine.min / b : null;
    var hi = isNum(qmax) ? qmax * routine.max / b : null;
    var routineRange = !near(routine.min, routine.max);
    var qtyRange = qual === 'finite_range' || qual === 'minimum' || qual === 'maximum';
    return {
      state: stateOf(lo, hi, qual), min: lo, max: hi,
      range_origin: routineRange && qtyRange ? 'mixed' : routineRange ? 'routine' : qtyRange ? 'declared_quantity' : null,
      reason_codes: []
    };
  }

  // Pack-derived conversions need matching physical units.
  function offerUsable(offer) {
    return offer && isNum(offer.total_gbp) && isNum(offer.pack_quantity) && offer.pack_quantity > 0 && !!offer.pack_unit;
  }

  function physicalBasisForCost(target, basis, offer) {
    if (!basis) return null;
    if (basis.kind === 'physical_units' && basis.pack_conversion && basis.unit === offer.pack_unit && isNum(basis.quantity) && basis.quantity > 0) return basis.quantity;
    // A per-day amount converts only when the physical daily amount is fixed and matches the pack unit.
    if (basis.kind === 'per_day' && target.routine.eligible && target.routine.qualifier === 'exact' &&
        target.routine.unit === offer.pack_unit && target.routine.min > 0) return target.routine.min;
    return null;
  }

  /**
   * Cost per k mg = k x P x b / (U x q). For q in [qmin,qmax]: [kPb/(U qmax), kPb/(U qmin)].
   * Independent of the daily routine. A zero lower quantity gives no finite upper cost.
   */
  function quantityCost(k, qmin, qmax, qual, b, offer) {
    var P = offer.total_gbp, U = offer.pack_quantity;
    if (qual === 'maximum') return { state: 'unavailable', reason_codes: ['MAXIMUM_ONLY'] };
    if (isNum(qmax) && qmax === 0) return { state: 'unavailable', reason_codes: ['ZERO_QUANTITY'] };
    var lo = isNum(qmax) ? k * P * b / (U * qmax) : null;
    var hi = isNum(qmin) && qmin > 0 ? k * P * b / (U * qmin) : null;
    if (qual === 'minimum' || qmax === null) return { state: hi !== null ? 'maximum_only' : 'unavailable', min: null, max: hi, reason_codes: ['MINIMUM_ONLY'] };
    return { state: stateOf(lo, hi, hi === null ? 'minimum' : qual), min: lo, max: hi, reason_codes: hi === null ? ['UNBOUNDED_COST'] : [] };
  }

  function findMeasurement(I, id) {
    for (var i = 0; i < I.measurements.length; i++) if (I.measurements[i].id === id) return I.measurements[i];
    return null;
  }

  /**
   * Marker mass per basis. Prefer a printed mass; otherwise linked quantity x percentage.
   * A printed mass is checked against the calculation using its printed rounding interval.
   */
  function markerPerBasis(target, I, K) {
    var codes = (K.reason_codes || []).slice();
    if (!K.eligible || K.state !== 'known') {
      if (codes.length === 0) codes.push(K.state === 'conflicting' ? 'FIELD_CONFLICT' : 'MARKER_NOT_PROVIDED');
      return { state: 'unavailable', reason_codes: codes, percentage: K.percentage };
    }
    var linked = K.linked_measurement_id ? findMeasurement(I, K.linked_measurement_id) : null;
    var calc = null;
    if (K.percentage && linked && linked.eligible && linked.state === 'known') {
      var pq = K.percentage.qualifier, lq = linked.qualifier;
      var lo = isNum(linked.min) && isNum(K.percentage.min) ? linked.min * K.percentage.min / 100 : null;
      var hi = isNum(linked.max) && isNum(K.percentage.max) ? linked.max * K.percentage.max / 100 : null;
      var q = (pq === 'minimum' || lq === 'minimum') ? 'minimum' : (pq === 'maximum' || lq === 'maximum') ? 'maximum'
        : (lo !== null && hi !== null && !near(lo, hi)) ? 'finite_range' : 'exact';
      if (q === 'minimum') hi = null;
      if (q === 'maximum') lo = null;
      calc = { min: lo, max: hi, qualifier: q, basis: basisFor(I, linked) };
    }
    if (K.reported) {
      var rb = K.basis && K.basis.kind !== 'unknown' ? K.basis : (linked ? basisFor(I, linked) : I.basis);
      if (K.basis && K.basis.kind === 'unknown' && K.basis.reason_codes && K.basis.reason_codes.indexOf('BASIS_MISSING') !== -1 && !linked) {
        return { state: 'unavailable', reason_codes: ['BASIS_MISSING'], percentage: K.percentage };
      }
      if (calc && K.origin === 'reported' && target.origin === 'user_entered' && K.reported.qualifier === 'exact' && calc.qualifier === 'exact' && calc.basis === rb) {
        // Printed-rounding compatibility check (section 6).
        var dp = isNum(K.reported_decimals) ? K.reported_decimals : 0;
        var half = 0.5 * Math.pow(10, -dp);
        if (lt(calc.min, K.reported.min - half) || gt(calc.min, K.reported.min + half)) {
          return { state: 'unavailable', reason_codes: ['FIELD_CONFLICT'], conflict: { reported_mg: K.reported.min, calculated_mg: calc.min }, percentage: K.percentage };
        }
      }
      return { state: stateOf(K.reported.min, K.reported.max, K.reported.qualifier), min: K.reported.min, max: K.reported.max,
        qualifier: K.reported.qualifier, basis: rb, origin: 'reported', reason_codes: [] };
    }
    if (calc) {
      return { state: stateOf(calc.min, calc.max, calc.qualifier), min: calc.min, max: calc.max, qualifier: calc.qualifier,
        basis: calc.basis, origin: 'calculated', linked_measurement_id: linked.id,
        reason_codes: calc.qualifier === 'minimum' ? ['MINIMUM_ONLY'] : [] };
    }
    if (K.percentage) {
      return { state: 'unavailable', reason_codes: [linked ? 'PARENT_AMOUNT_UNAVAILABLE' : 'MARKER_NOT_LINKED'], percentage: K.percentage };
    }
    return { state: 'unavailable', reason_codes: ['MARKER_NOT_PROVIDED'] };
  }

  var PREP_METRIC = { extract: 'extract', powder: 'powder', biomass: 'biomass', unspecified: 'material' };

  /**
   * All local results for one normalized label. Returns { metric_id: result }.
   * Benchmarkable metrics carry pool_eligible = true when the result may enter
   * (or be compared against) a market pool.
   */
  function calculateLocal(target) {
    var out = {};
    var offer = target.offer;
    var offerOk = offerUsable(offer);
    var offerPoolOk = offerOk && offer.orderability !== 'unavailable';

    INGREDIENTS.forEach(function (ing) {
      var I = target.ingredients[ing];
      if (!I) return;
      var routine = routineFor(target, I);

      I.measurements.forEach(function (m) {
        var prep = PREP_METRIC[m.preparation] || 'material';
        var codes = (m.reason_codes || []).slice();
        // Extract that is not reliably classified is kept as material, never ranked as extract.
        if (prep === 'extract' && !m.extract_eligible) { prep = 'material'; if (codes.indexOf('EXTRACT_CLASSIFICATION_CONFLICT') === -1) codes.push('PREPARATION_UNCLEAR'); }
        if (!m.eligible || m.state !== 'known') {
          out[ing + '_' + prep + '_mg_per_basis'] = result(ing + '_' + prep + '_mg_per_basis', { reason_codes: codes.concat(['AMOUNT_MEANING_UNCLEAR']) });
          return;
        }
        var basis = basisFor(I, m);
        var pb = ing + '_' + prep + '_mg_per_basis';
        out[pb] = result(pb, {
          calculation_state: stateOf(m.min, m.max, m.qualifier), value: value(m.min, m.max, 'mg', { basis: describeBasis(basis) }),
          provenance: { origin: m.origin === 'user_entered' ? 'user_entered' : 'reported', dependencies: [m.id] }, reason_codes: codes
        });
        var daily = dailyAmount(m.min, m.max, m.qualifier, basis, routine);
        var dm = ing + '_' + prep + '_mg_day';
        out[dm] = result(dm, {
          calculation_state: daily.state, value: daily.state === 'unavailable' ? null : value(daily.min, daily.max, 'mg/day'),
          range_origin: daily.range_origin, reason_codes: codes.concat(daily.reason_codes),
          provenance: { origin: target.origin === 'user_entered' ? 'user_entered' : 'calculated', dependencies: [m.id, 'routine'] },
          pool_eligible: (prep === 'extract' || prep === 'powder') && daily.state !== 'unavailable'
        });
        if (prep === 'extract') {
          var cm = 'cost_per_100mg_' + ing + '_extract_gbp';
          out[cm] = costResult(cm, EXTRACT_K, m.min, m.max, m.qualifier, basis, m.id, codes);
          if (I.der && isNum(I.der.value) && I.der.linked && daily.state !== 'unavailable') {
            var rm = ing + '_raw_equivalent_mg_day';
            out[rm] = result(rm, {
              calculation_state: daily.state, value: value(daily.min * I.der.value, isNum(daily.max) ? daily.max * I.der.value : null, 'mg/day'),
              provenance: { origin: 'calculated', dependencies: [m.id, 'der'] }, reason_codes: ['DESCRIPTIVE_ONLY']
            });
          }
        }
      });
      if (I.headline) {
        out[ing + '_headline'] = result(ing + '_headline', { calculation_state: 'unavailable', quoted: I.headline.text, reason_codes: [I.headline.reason || 'AMOUNT_MEANING_UNCLEAR'] });
      }

      I.markers.forEach(function (K) {
        var mk = markerPerBasis(target, I, K);
        var base = K.name + '_mg_per_basis';
        if (mk.state === 'unavailable') {
          out[K.name + '_mg_day'] = result(K.name + '_mg_day', { reason_codes: mk.reason_codes, percentage: mk.percentage || null, conflict: mk.conflict || null });
          return;
        }
        out[base] = result(base, {
          calculation_state: mk.state, value: value(mk.min, mk.max, 'mg', { basis: describeBasis(mk.basis) }),
          provenance: { origin: target.origin === 'user_entered' ? 'user_entered' : mk.origin, dependencies: [K.linked_measurement_id || K.name] },
          reason_codes: mk.reason_codes
        });
        var daily = dailyAmount(mk.min, mk.max, mk.qualifier, mk.basis, routineFor(target, I));
        var dm = K.name + '_mg_day';
        out[dm] = result(dm, {
          calculation_state: daily.state, value: daily.state === 'unavailable' ? null : value(daily.min, daily.max, 'mg/day'),
          range_origin: daily.range_origin, reason_codes: mk.reason_codes.concat(daily.reason_codes),
          provenance: { origin: target.origin === 'user_entered' ? 'user_entered' : mk.origin, dependencies: [K.linked_measurement_id || K.name, 'routine'] },
          pool_eligible: daily.state === 'exact' || daily.state === 'bounded'
        });
        var k = MARKERS[K.name].k;
        var cm = 'cost_per_' + k + 'mg_' + K.name + '_gbp';
        out[cm] = costResult(cm, k, mk.min, mk.max, mk.qualifier, mk.basis, K.linked_measurement_id || K.name, mk.reason_codes);
      });
    });

    // Purchase arithmetic (pack-level)
    if (offer) {
      if (!offerOk) {
        out.cost_per_pack_unit_gbp = result('cost_per_pack_unit_gbp', { reason_codes: offer.reason_codes.concat(['PACK_INCOMPLETE']) });
      } else {
        var P = offer.total_gbp, U = offer.pack_quantity;
        out.recorded_pack_price_gbp = result('recorded_pack_price_gbp', {
          calculation_state: 'exact', value: value(P, P, 'GBP', { pack_quantity: U, pack_unit: offer.pack_unit }), reason_codes: ['DESCRIPTIVE_ONLY']
        });
        out.cost_per_pack_unit_gbp = result('cost_per_pack_unit_gbp', {
          calculation_state: 'exact', value: value(P / U, P / U, 'GBP per ' + offer.pack_unit), reason_codes: []
        });
        var R = target.routine;
        if (R.eligible && R.state === 'known' && R.unit === offer.pack_unit) {
          // Daily cost P x d / U and days of supply U / d (reversed endpoints).
          var dc = value(P * R.min / U, P * R.max / U, 'GBP/day');
          out.daily_cost_gbp = result('daily_cost_gbp', {
            calculation_state: stateOf(dc.min, dc.max, 'exact'), value: dc, range_origin: near(R.min, R.max) ? null : 'routine',
            provenance: { origin: target.origin === 'user_entered' ? 'user_entered' : 'calculated', dependencies: ['offer', 'routine'] },
            pool_eligible: offerPoolOk, reason_codes: offerPoolOk ? [] : ['OFFER_UNAVAILABLE']
          });
          if (R.max === 0) {
            out.days_of_supply = result('days_of_supply', { reason_codes: ['ZERO_DAILY_USE'] });
          } else {
            var sMin = U / R.max, sMax = R.min > 0 ? U / R.min : null;
            out.days_of_supply = result('days_of_supply', {
              calculation_state: sMax === null ? 'minimum_only' : stateOf(sMin, sMax, 'exact'), value: value(sMin, sMax, 'days'),
              range_origin: near(R.min, R.max) ? null : 'routine', reason_codes: []
            });
          }
        } else {
          var why = !R.eligible || R.state !== 'known' ? 'DAILY_FREQUENCY_UNKNOWN' : 'UNIT_UNMATCHED';
          out.daily_cost_gbp = result('daily_cost_gbp', { reason_codes: [why] });
          out.days_of_supply = result('days_of_supply', { reason_codes: [why] });
        }
        // Cost per label basis (shared basis, or the first ingredient basis that converts).
        var cb = null;
        INGREDIENTS.forEach(function (ing) {
          var I = target.ingredients[ing];
          if (cb || !I) return;
          var b = physicalBasisForCost(target, I.basis, offer);
          if (b !== null && I.basis.kind === 'physical_units') cb = { b: b, basis: I.basis };
        });
        if (cb) {
          out.cost_per_basis_gbp = result('cost_per_basis_gbp', {
            calculation_state: 'exact', value: value(P * cb.b / U, P * cb.b / U, 'GBP', { basis: describeBasis(cb.basis) }), reason_codes: []
          });
        }
      }
      // Days of supply needs only the pack quantity and the daily use, not the price.
      var R0 = target.routine;
      if (!offerOk && isNum(offer.pack_quantity) && offer.pack_quantity > 0 && offer.pack_unit &&
          R0.eligible && R0.state === 'known' && R0.unit === offer.pack_unit && R0.max > 0) {
        var s0 = offer.pack_quantity / R0.max, s1 = R0.min > 0 ? offer.pack_quantity / R0.min : null;
        out.days_of_supply = result('days_of_supply', {
          calculation_state: s1 === null ? 'minimum_only' : stateOf(s0, s1, 'exact'), value: value(s0, s1, 'days'),
          range_origin: near(R0.min, R0.max) ? null : 'routine', reason_codes: []
        });
      }
    }

    function costResult(id, k, qmin, qmax, qual, basis, dep, codes) {
      if (!offer) return result(id, { reason_codes: ['PRICE_NOT_PROVIDED'] });
      if (!offerOk) return result(id, { reason_codes: ['PACK_INCOMPLETE'] });
      var b = physicalBasisForCost(target, basis, offer);
      if (b === null) return result(id, { reason_codes: ['UNIT_UNMATCHED'] });
      var c = quantityCost(k, qmin, qmax, qual, b, offer);
      return result(id, {
        calculation_state: c.state, value: c.state === 'unavailable' ? null : value(c.min, c.max, k === 1 ? 'GBP per 1 mg' : 'GBP per 100 mg'),
        provenance: { origin: target.origin === 'user_entered' ? 'user_entered' : 'calculated', dependencies: [dep, 'offer'] },
        pool_eligible: offerPoolOk && (c.state === 'exact' || c.state === 'bounded'),
        reason_codes: (codes || []).concat(c.reason_codes || []).concat(offerPoolOk ? [] : ['OFFER_UNAVAILABLE'])
      });
    }
    return out;
  }

  var PLURAL = { capsule: 'capsules', tablet: 'tablets', gummy: 'gummies', sachet: 'sachets', scoop: 'scoops', pipette: 'pipettes', ml: 'mL' };
  function unitText(unit, qty) {
    if (unit === 'ml') return 'mL';
    return qty === 1 ? unit : (PLURAL[unit] || unit);
  }

  function describeBasis(b) {
    if (!b) return null;
    if (b.kind === 'per_day') return { kind: 'per_day', text: 'per day' };
    if (b.kind === 'physical_units') return { kind: 'physical_units', quantity: b.quantity, unit: b.unit, text: 'per ' + b.quantity + ' ' + unitText(b.unit, b.quantity) };
    var q = isNum(b.quantity) && b.quantity > 0 ? b.quantity : null;
    var u = b.unit && b.unit !== 'unconverted_unit' ? unitText(b.unit, q) : (q === 1 ? 'label unit' : 'label units');
    return { kind: b.kind, quantity: b.quantity, unit: b.unit, text: q !== null ? 'per ' + q + ' ' + u : 'per serving (size not given)' };
  }

  /* ------------------------------------------------------------------------
     6. Market pools (section 7)
     ------------------------------------------------------------------------ */

  var BENCHMARK_METRICS = {
    ashwagandha_extract_mg_day: { direction: 'amount', title: 'Ashwagandha extract per day', unit: 'mg/day' },
    lions_mane_extract_mg_day: { direction: 'amount', title: 'Lion’s mane extract per day', unit: 'mg/day' },
    ashwagandha_powder_mg_day: { direction: 'amount', title: 'Ashwagandha powder per day', unit: 'mg/day' },
    lions_mane_powder_mg_day: { direction: 'amount', title: 'Lion’s mane powder per day', unit: 'mg/day' },
    withanolides_mg_day: { direction: 'amount', title: 'Withanolides per day', unit: 'mg/day' },
    polysaccharides_mg_day: { direction: 'amount', title: 'Polysaccharides per day', unit: 'mg/day' },
    beta_glucans_mg_day: { direction: 'amount', title: 'Beta-glucans per day', unit: 'mg/day' },
    daily_cost_gbp: { direction: 'cost', title: 'Cost per day', unit: 'GBP/day', price: true },
    cost_per_100mg_ashwagandha_extract_gbp: { direction: 'cost', title: 'Cost per 100 mg ashwagandha extract', unit: 'GBP', price: true },
    cost_per_100mg_lions_mane_extract_gbp: { direction: 'cost', title: 'Cost per 100 mg lion’s mane extract', unit: 'GBP', price: true },
    cost_per_1mg_withanolides_gbp: { direction: 'cost', title: 'Cost per 1 mg withanolides', unit: 'GBP', price: true },
    cost_per_100mg_polysaccharides_gbp: { direction: 'cost', title: 'Cost per 100 mg polysaccharides', unit: 'GBP', price: true },
    cost_per_100mg_beta_glucans_gbp: { direction: 'cost', title: 'Cost per 100 mg beta-glucans', unit: 'GBP', price: true }
  };

  /**
   * Index the released dataset once (per dataset_version): normalizes every
   * record and computes its local results. Never mutates the dataset.
   */
  var indexCache = {};
  function indexDataset(dataset) {
    var key = dataset.dataset_version + '|' + dataset.exported_at;
    if (indexCache[key]) return indexCache[key];
    var entries = dataset.formula_records.map(function (rec) {
      var t = targetFromFormula(rec);
      return { id: rec.formula_id, record: rec, target: t, local: calculateLocal(t) };
    });
    indexCache = {}; // keep one dataset only
    indexCache[key] = { dataset: dataset, entries: entries };
    return indexCache[key];
  }

  // Reproducible, named cohort filter (no automatic "best match").
  function passesFilter(rec, filter) {
    if (!filter) return true;
    if (filter.format && rec.format !== filter.format) return false;
    if (filter.tier && rec.tier !== filter.tier) return false;
    return true;
  }

  function referenceUniverse(index, filter) {
    return index.entries.filter(function (e) {
      return e.record.status === 'active' && e.record.adult_comparator === true && passesFilter(e.record, filter);
    });
  }

  /** Build one metric pool after removing the identified target once. */
  function buildPool(index, metricId, options) {
    options = options || {};
    var universe = referenceUniverse(index, options.filter);
    var T0 = universe.length;
    var excluded = options.exclude_formula_id || null;
    var peers = universe.filter(function (e) { return e.id !== excluded; });
    var finite = [], exact = [], exclusions = [];
    peers.forEach(function (e) {
      var r = e.local[metricId];
      if (!r || !r.pool_eligible || (r.calculation_state !== 'exact' && r.calculation_state !== 'bounded')) {
        exclusions.push({ formula_id: e.id, reason_codes: r ? (r.reason_codes.length ? r.reason_codes : [r.calculation_state]) : ['NOT_RECORDED'] });
        return;
      }
      finite.push({ id: e.id, interval: [r.value.min, r.value.max] });
      if (r.calculation_state === 'exact') exact.push({ id: e.id, value: r.value.min });
    });
    return {
      metric_id: metricId, T: peers.length, T_before_exclusion: T0, target_excluded: T0 !== peers.length,
      finite: finite, exact: exact, n: finite.length, exact_n: exact.length, exclusion_log: exclusions
    };
  }

  /* ------------------------------------------------------------------------
     7. Compare: the full response contract (section 12)
     ------------------------------------------------------------------------ */

  function scopeCheck(target) {
    var s = target.scope || {};
    if (s.status && s.status !== 'active') return { state: 'out_of_scope', code: 'OUT_OF_SCOPE' };
    if (s.ashwagandha_present === false || s.lions_mane_present === false) return { state: 'out_of_scope', code: 'OUT_OF_SCOPE', reason: 'single_ingredient' };
    if (s.age_population === 'children' || s.age_population === 'mixed_unresolved') return { state: 'out_of_scope', code: 'OUT_OF_SCOPE', reason: 'age_population' };
    if (s.purpose_eligible === false) return { state: 'out_of_scope', code: 'OUT_OF_SCOPE', reason: 'product_type' };
    if (target.origin === 'catalogue') return s.adult_comparator ? { state: 'in_scope' } : { state: 'out_of_scope', code: 'OUT_OF_SCOPE' };
    var unconfirmed = [];
    if (s.ashwagandha_present !== true) unconfirmed.push('ashwagandha_present');
    if (s.lions_mane_present !== true) unconfirmed.push('lions_mane_present');
    if (s.age_population !== 'adult_general') unconfirmed.push('age_population');
    return unconfirmed.length ? { state: 'unconfirmed', code: 'SCOPE_UNCONFIRMED', fields: unconfirmed } : { state: 'in_scope' };
  }

  function priceScenarioCheck(target) {
    var o = target.offer;
    if (!o) return { ok: false, code: 'PRICE_NOT_PROVIDED' };
    if (o.orderability === 'unavailable') return { ok: false, code: 'OFFER_UNAVAILABLE' };
    if (o.price_class === 'recorded_normal_list') return { ok: true, comparability: target.origin === 'catalogue' ? 'matched' : 'personal_scenario' };
    if (o.price_class === 'one_time') return { ok: true, comparability: 'personal_scenario' };
    if (o.price_class === 'unknown') return { ok: false, code: 'PRICE_CLASS_UNKNOWN' };
    return { ok: false, code: 'PRICE_CLASS_MISMATCH' };
  }

  /**
   * Main entry point.
   *   target   normalized label model (targetFromFormula or buildManualTarget().target)
   *   dataset  released dataset object, or null if it failed to load
   *   scenario { filter: {format, tier} } optional
   * Returns the response object: versions, target, results (ordered), states.
   */
  function compare(target, dataset, scenario) {
    scenario = scenario || {};
    var local = calculateLocal(target);
    var datasetState = dataset ? checkSchema(dataset) : { ok: false, code: 'DATASET_UNAVAILABLE' };
    var index = datasetState.ok ? indexDataset(dataset) : null;
    var scope = scopeCheck(target);
    var price = priceScenarioCheck(target);
    var excludeId = target.source_formula_id || null;
    if (index && excludeId && !index.entries.some(function (e) { return e.id === excludeId; })) excludeId = null;

    var results = [];
    Object.keys(local).forEach(function (id) {
      var r = local[id];
      var meta = BENCHMARK_METRICS[id];
      var res = {
        metric_id: id, title: meta ? meta.title : titleFor(id), calculation_state: r.calculation_state,
        value: r.value, range_origin: r.range_origin, provenance: r.provenance, reason_codes: r.reason_codes.slice(),
        quoted: r.quoted || null, percentage: r.percentage || null, conflict: r.conflict || null,
        benchmark_state: 'unavailable', cohort: null, position: null, summary: null
      };
      if (meta && r.calculation_state !== 'unavailable') benchmark(res, r, meta);
      res.interpretation = interpretation(res, target);
      res.presentation = present(res, target, meta);
      results.push(res);
    });
    results.sort(function (a, b) { return orderOf(a.metric_id) - orderOf(b.metric_id) || (a.metric_id < b.metric_id ? -1 : 1); });

    var usable = results.filter(function (r) { return r.calculation_state !== 'unavailable' && r.calculation_state !== 'invalid'; });
    return {
      versions: {
        engine_version: ENGINE_VERSION,
        schema_version: dataset ? dataset.schema_version : null,
        dataset_version: dataset ? dataset.dataset_version : null,
        method_version: dataset ? dataset.method_version : null,
        research_snapshot_date: dataset ? dataset.research_snapshot_date : null
      },
      target: {
        origin: target.origin, source_formula_id: target.source_formula_id || null,
        external: !target.source_formula_id, personal_edits: !!target.edited, input_hash: hashInput(target)
      },
      dataset_state: datasetState.ok ? 'available' : datasetState.code,
      scope_state: scope,
      price_scenario: price,
      overall_state: usable.length ? 'results' : 'NO_USABLE_INPUT',
      next_input: usable.length ? null : 'Add one labelled amount and its basis, or a price and pack quantity, to get started.',
      primary: primaryIds(usable),
      results: results
    };

    function benchmark(res, r, meta) {
      if (!index) { res.benchmark_state = 'unavailable'; res.reason_codes.push(datasetState.code); return; }
      if (scope.state !== 'in_scope') { res.benchmark_state = scope.state === 'out_of_scope' ? 'out_of_scope' : 'unavailable'; res.reason_codes.push(scope.code); return; }
      if (meta.price && !price.ok) { res.benchmark_state = 'unavailable'; res.reason_codes.push(price.code); return; }
      if (target.origin === 'catalogue' && !r.pool_eligible && meta.price) { res.benchmark_state = 'unavailable'; return; }
      var pool = buildPool(index, res.metric_id, { exclude_formula_id: excludeId, filter: scenario.filter });
      var n = pool.n;
      res.benchmark_state = benchmarkStateFor(n);
      res.cohort = {
        metric_id: res.metric_id, filter: scenario.filter || null, price_class: meta.price ? 'recorded_normal_list' : null,
        snapshot_date: dataset.research_snapshot_date, eligible_ids: pool.finite.map(function (p) { return p.id; }),
        n: n, T: pool.T, n_before_exclusion: n + (pool.target_excluded && index.entries.some(function (e) {
          return e.id === excludeId && e.local[res.metric_id] && e.local[res.metric_id].pool_eligible;
        }) ? 1 : 0),
        T_before_exclusion: pool.T_before_exclusion, exact_reference_n: pool.exact_n, exclusion_log: pool.exclusion_log
      };
      var v = r.value;
      // Minimum-only / maximum-only results never enter finite positions (section 6).
      if (r.calculation_state === 'minimum_only' || r.calculation_state === 'maximum_only') {
        res.reason_codes.push(r.calculation_state === 'minimum_only' ? 'MINIMUM_ONLY' : 'MAXIMUM_ONLY');
        res.position = null;
      } else if (n > 0) {
        var ip = intervalPosition(v.min, v.max, pool.finite.map(function (p) { return p.interval; }), meta.direction);
        var pos = {
          direction: meta.direction, n: n, definite_count: ip.definite_count, possible_count: ip.possible_count,
          percent_bounds: ip.percent_bounds, display_bounds: ip.display_bounds,
          strict_count: null, tie_count: null, strict_percent: null, exact_reference_n: pool.exact_n
        };
        if (r.calculation_state === 'exact' && pool.exact_n > 0) {
          var ep = exactPosition(v.min, pool.exact.map(function (p) { return p.value; }), meta.direction);
          pos.strict_count = ep.strict_count; pos.tie_count = ep.tie_count; pos.strict_percent = ep.strict_percent;
          pos.strict_display_percent = Math.floor(ep.strict_percent + 1e-9);
        }
        res.position = pos;
      }
      var s = summarise(pool.exact.map(function (p) { return p.value; }));
      s.band = pool.exact_n >= 10 && r.calculation_state !== 'minimum_only' && r.calculation_state !== 'maximum_only'
        ? bandSpan(v.min, v.max, s, meta.direction) : null;
      s.band_pool = 'fixed_daily_routines_only';
      res.summary = s;
    }
  }

  var ORDER = ['ashwagandha_extract_mg_day', 'ashwagandha_powder_mg_day', 'ashwagandha_material_mg_day', 'ashwagandha_biomass_mg_day',
    'lions_mane_extract_mg_day', 'lions_mane_powder_mg_day', 'lions_mane_biomass_mg_day', 'lions_mane_material_mg_day',
    'daily_cost_gbp', 'days_of_supply', 'withanolides_mg_day', 'polysaccharides_mg_day', 'beta_glucans_mg_day'];
  function orderOf(id) { var i = ORDER.indexOf(id); return i === -1 ? 100 : i; }

  // Up to three primary groups: daily ingredient amounts (Ash then LM), then daily cost.
  // Falls back to per-basis amounts or pack costs when daily results are unavailable.
  function primaryIds(usable) {
    var ids = usable.map(function (r) { return r.metric_id; });
    var pick = [];
    INGREDIENTS.forEach(function (ing) {
      var daily = ['extract', 'powder', 'material', 'biomass'].map(function (p) { return ing + '_' + p + '_mg_day'; })
        .filter(function (id) { return ids.indexOf(id) !== -1; })[0];
      var perBasis = ['extract', 'powder', 'material', 'biomass'].map(function (p) { return ing + '_' + p + '_mg_per_basis'; })
        .filter(function (id) { return ids.indexOf(id) !== -1; })[0];
      if (daily || perBasis) pick.push(daily || perBasis);
    });
    var cost = ['daily_cost_gbp', 'cost_per_basis_gbp', 'cost_per_pack_unit_gbp'].filter(function (id) { return ids.indexOf(id) !== -1; })[0];
    if (cost) pick.push(cost);
    return pick.slice(0, 3);
  }

  function titleFor(id) {
    var t = {
      cost_per_pack_unit_gbp: 'Cost per unit in the pack', cost_per_basis_gbp: 'Cost per label serving',
      days_of_supply: 'Days the pack lasts', recorded_pack_price_gbp: 'Pack price'
    }[id];
    if (t) return t;
    var parts = id.replace(/_gbp$/, '').split('_');
    var s = parts.join(' ').replace('lions mane', 'lion’s mane').replace(' mg per basis', ' per label basis').replace(' mg day', ' per day');
    return s.charAt(0).toUpperCase() + s.slice(1);
  }

  /* ------------------------------------------------------------------------
     8. Interpretation and plain-language presentation (sections 8 and 9)
     ------------------------------------------------------------------------ */

  function interpretation(res, target) {
    var reliability = target.origin === 'user_entered' ? 'calculated_from_your_entries'
      : (res.reason_codes.indexOf('FIELD_CONFLICT') !== -1 ? 'unresolved' : 'clear_source_and_calculation');
    var comparability = /extract|powder/.test(res.metric_id) ? 'broad_labelled_quantity_comparison'
      : /withanolides|polysaccharides|beta_glucans/.test(res.metric_id) ? 'reported_label_values_assay_not_established' : 'not_applicable';
    return {
      reliability: reliability, comparability: comparability,
      coverage: res.cohort ? res.cohort.n + '/' + res.cohort.T : null,
      reason_codes: uniq(res.reason_codes)
    };
  }

  function uniq(a) { return a.filter(function (x, i) { return a.indexOf(x) === i; }); }

  function formatMg(x) {
    if (!isNum(x)) return '';
    var r = Math.abs(x) >= 100 ? Math.round(x) : Math.abs(x) >= 10 ? Math.round(x * 10) / 10 : Math.round(x * 100) / 100;
    return r.toLocaleString('en-GB', { maximumFractionDigits: 2 }) + ' mg';
  }

  // Two decimals unless that hides a positive small value (section 8).
  function formatGBP(x) {
    if (!isNum(x)) return '';
    if (x > 0 && Math.round(x * 100) === 0) {
      var s = x.toFixed(4).replace(/0+$/, '');
      return Number(s) === 0 ? 'less than £0.0001' : '£' + s;
    }
    return '£' + x.toFixed(2);
  }

  function formatDays(x) { return isNum(x) ? (Math.round(x * 10) / 10).toLocaleString('en-GB') + ' days' : ''; }

  function formatValue(res) {
    var v = res.value;
    if (!v) return res.quoted ? '“' + res.quoted + '” (as printed)' : '';
    var f = /gbp/.test(res.metric_id) ? formatGBP : res.metric_id === 'days_of_supply' ? formatDays : formatMg;
    var suffix = /_mg_day$/.test(res.metric_id) || res.metric_id === 'daily_cost_gbp' ? ' per day' : '';
    if ((/_mg_per_basis$/.test(res.metric_id) || res.metric_id === 'cost_per_basis_gbp') && v.basis) suffix = ' ' + v.basis.text;
    var qc = res.metric_id.match(/^cost_per_(1|100)mg_(.+)_gbp$/);
    if (qc) suffix = ' per ' + qc[1] + ' mg ' + qc[2].replace('lions_mane', 'lion\u2019s mane').replace(/_/g, ' ').replace('beta glucans', 'beta-glucans');
    if (res.metric_id === 'cost_per_pack_unit_gbp') suffix = ' per ' + v.unit.replace('GBP per ', '');
    if (res.metric_id === 'recorded_pack_price_gbp') suffix = ' for ' + v.pack_quantity + ' ' + unitText(v.pack_unit, v.pack_quantity);
    if (res.calculation_state === 'minimum_only') return 'at least ' + f(v.min) + suffix;
    if (res.calculation_state === 'maximum_only') return 'up to ' + f(v.max) + suffix;
    if (res.calculation_state === 'exact') return f(v.min) + suffix;
    return f(v.min).replace(/ (mg|days)$/, '') + '–' + f(v.max) + suffix; // 150–300 mg, 30–60 days
  }

  var NEXT_INPUT = {
    BASIS_MISSING: 'Tell us what the amount is based on, for example per 2 capsules.',
    DAILY_FREQUENCY_UNKNOWN: 'Add the recommended daily directions to see a daily amount.',
    UNIT_UNMATCHED: 'Use the same unit for the pack, the serving and the daily directions, or add the label’s conversion.',
    MARKER_NOT_LINKED: 'Say which amount the percentage applies to.',
    PARENT_AMOUNT_UNAVAILABLE: 'Add the amount of the ingredient that the percentage applies to.',
    MARKER_NOT_PROVIDED: 'Add a printed marker amount or percentage if your label has one.',
    PRICE_NOT_PROVIDED: 'Add the pack price and pack quantity.',
    PACK_INCOMPLETE: 'Add the pack price, total quantity and its unit.',
    PRICE_CLASS_UNKNOWN: 'Say whether the price is a normal one-time price, a promotion or a subscription.',
    SCOPE_UNCONFIRMED: 'Confirm the product contains both ingredients and is intended for adults to see a market comparison.'
  };

  function present(res, target, meta) {
    var name = MARKERS[res.metric_id.replace(/_mg_day$/, '')] ? MARKERS[res.metric_id.replace(/_mg_day$/, '')].label : null;
    var formatted = formatValue(res);
    var codes = res.reason_codes;
    var text = '';
    var c = res.cohort, p = res.position;
    var next = null;
    for (var i = 0; i < codes.length && !next; i++) next = NEXT_INPUT[codes[i]] || null;

    if (res.calculation_state === 'unavailable') {
      if (res.quoted && codes.indexOf('EQUIVALENT_NOT_REVERSED') !== -1) text = 'The label gives ' + formatted + ' as an “equivalent to” amount: the starting plant or mushroom, not the extract itself. It is shown as printed and not used in calculations.';
      else if (res.quoted && codes.indexOf('BLEND_NOT_ALLOCATED') !== -1) text = 'The label gives ' + formatted + ' as a blend total that is not split between ingredients, so it is not used in calculations.';
      else if (res.quoted) text = 'The label gives ' + formatted + '. Its meaning is not clear enough to treat it as an actual extract or material amount, so it is not used in calculations.';
      else if (name && codes.indexOf('FIELD_CONFLICT') !== -1) text = 'The printed ' + name + ' amount does not match the amount calculated from the percentage, so this result is held back for checking.';
      else if (name && res.percentage) text = 'Your label states ' + res.percentage.min + '% ' + name + '. We cannot calculate a ' + name + ' amount because the matching ingredient amount was not available.';
      else if (name) text = 'We cannot calculate ' + name + ' because a matching percentage or marker amount was not provided.';
      else if (codes.indexOf('DAILY_FREQUENCY_UNKNOWN') !== -1) text = 'A serving size does not tell us how often it is recommended, so no daily figure is shown.';
      else text = 'This result needs more information.';
      return { title: res.title, formatted_value: '', explanation: text, next_input: next };
    }

    var isAmount = meta && meta.direction === 'amount';
    var prep = /extract/.test(res.metric_id) ? 'extract' : /powder/.test(res.metric_id) ? 'powder' : '';
    var priceWord = target.origin === 'catalogue' ? 'recorded' : 'entered';
    if (!meta) {
      if (res.metric_id === 'days_of_supply') text = 'At the recommended daily amount, the pack lasts ' + formatted + '.';
      else if (res.metric_id === 'recorded_pack_price_gbp') text = 'The ' + priceWord + ' pack price is ' + formatted + '.';
      else if (/gbp$/.test(res.metric_id)) text = 'At the ' + priceWord + ' pack price, this works out at ' + formatted + ', excluding delivery.';
      else if (/_material_mg_/.test(res.metric_id)) text = 'The label gives ' + formatted + ' of material. It does not establish an extract amount, so we have kept it out of extract comparisons.';
      else if (/_biomass_mg_/.test(res.metric_id)) text = 'The label gives ' + formatted + ' of biomass. Biomass is shown separately and is not added to the extract amount.';
      else if (/_raw_equivalent_/.test(res.metric_id)) text = 'Using the label\u2019s linked ratio, this is equivalent to ' + formatted + ' of starting material. It is descriptive only.';
      else text = 'The label gives ' + formatted + '.';
    } else if (/_material_mg_/.test(res.metric_id)) {
      text = 'The label gives ' + formatted + ' of material. It does not establish an extract amount, so we have kept it out of extract comparisons.';
    } else if (meta && res.benchmark_state === 'no_peers') {
      text = 'Your label works out at ' + formatted + '. No comparable records are available for this measurement.';
    } else if (meta && p && (res.benchmark_state === 'available' || res.benchmark_state === 'small_subset')) {
      var small = res.benchmark_state === 'small_subset';
      if (res.calculation_state === 'exact' && p.definite_count === p.possible_count) {
        text = isAmount
          ? 'Your label provides ' + formatted + (prep ? ' of ' + prep : '') + '. This is higher than ' + p.definite_count + ' of ' + c.n + ' comparable reviewed formulas.'
          : 'At the ' + priceWord + ' pack price, this works out at ' + formatted + ', excluding delivery. It is less expensive than ' + p.definite_count + ' of ' + c.n + ' comparable reviewed formulas.';
      } else {
        text = isAmount
          ? 'Your label supports ' + formatted + '. That is higher than at least ' + p.definite_count + ' and potentially ' + p.possible_count + ' of ' + c.n + ' comparable reviewed formulas, depending on their supported label ranges or routines.'
          : 'This works out at ' + formatted + ', excluding delivery. It is less expensive than at least ' + p.definite_count + ' and potentially ' + p.possible_count + ' of ' + c.n + ' comparable reviewed formulas.';
      }
      if (small) text += ' Only ' + c.n + ' reviewed formulas have comparable information, so no market-position badge is shown.';
      else if (isAmount && prep) text += ' Labelled amount does not establish equivalent effects between preparations.';
      else if (isAmount && name) text += ' Test methods are rarely stated, so this compares reported label values only.';
      if (meta.price) text += ' The comparison uses recorded normal/list prices from the dated database, not live checkout prices.';
    } else if (meta && res.benchmark_state === 'insufficient_peers') {
      text = 'Your label works out at ' + formatted + '. Only ' + c.n + ' reviewed formulas have comparable information for this measurement, which is too little coverage for a market position.';
    } else if (res.calculation_state === 'minimum_only' || res.calculation_state === 'maximum_only') {
      text = 'Your label supports ' + formatted + '. An “at least” or “at most” amount is shown as a bound and is not given an exact market position.';
    } else if (meta && res.benchmark_state === 'out_of_scope') {
      text = 'Your label works out at ' + formatted + '. This product is outside the adult combined-formula database, so no market comparison is shown.';
    } else if (meta && codes.indexOf('SCOPE_UNCONFIRMED') !== -1) {
      text = 'Your label works out at ' + formatted + '. A market comparison needs confirmation that the product contains both ingredients and is intended for adults.';
    } else if (meta && meta.price && codes.some(function (x) { return /^PRICE_CLASS|OFFER_UNAVAILABLE/.test(x); })) {
      text = 'At the entered price this works out at ' + formatted + ', excluding delivery. It is not compared with the recorded prices because the price type does not match.';
    } else {
      text = (/gbp/.test(res.metric_id) ? 'At the entered price this works out at ' : 'Your label works out at ') + formatted + '.';
    }
    if (target.origin === 'user_entered') text += ' Calculated from your entries.';
    return { title: res.title, formatted_value: formatted, explanation: text, next_input: next };
  }

  /* ------------------------------------------------------------------------
     9. Information coverage checklist (section 10)
     ------------------------------------------------------------------------ */

  var COVERAGE_STATES = ['explicit_consistent', 'not_found', 'unclear', 'conflicting', 'not_assessed'];
  function coverageSummary(fieldStates) {
    var assessed = 0, explicit = 0;
    fieldStates.forEach(function (s) {
      if (COVERAGE_STATES.indexOf(s) === -1 || s === 'not_assessed') return;
      assessed++;
      if (s === 'explicit_consistent') explicit++;
    });
    var complete = assessed === 10 && fieldStates.length === 10;
    return {
      denominator: 10, assessed_count: assessed, explicit_count: explicit,
      score_percent: complete ? 100 * explicit / 10 : null,
      label: complete ? (100 * explicit / 10) + '% of 10 core details found' : assessed + ' of 10 checked'
    };
  }

  /* ------------------------------------------------------------------------
     10. Schema check, hashing, catalogue list
     ------------------------------------------------------------------------ */

  function checkSchema(dataset) {
    if (!dataset || typeof dataset !== 'object') return { ok: false, code: 'SCHEMA_INVALID' };
    var major = parseInt(String(dataset.schema_version || '').split('.')[0], 10);
    if (major !== SUPPORTED_SCHEMA_MAJOR || !Array.isArray(dataset.formula_records)) return { ok: false, code: 'SCHEMA_INVALID' };
    var seen = {};
    for (var i = 0; i < dataset.formula_records.length; i++) {
      var r = dataset.formula_records[i];
      if (!r || !r.formula_id) return { ok: false, code: 'SCHEMA_INVALID' };
      if (r.status === 'active') {
        if (seen[r.formula_id]) return { ok: false, code: 'SCHEMA_INVALID' };
        seen[r.formula_id] = true;
      }
    }
    return { ok: true };
  }

  function hashInput(obj) {
    var s = JSON.stringify(obj, function (k, v) { return k === 'coverage' ? undefined : v; }) || '';
    var h = 5381;
    for (var i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
    return (h >>> 0).toString(16);
  }

  /** Reviewed products for the "Choose a reviewed product" list (active only). */
  function catalogueOptions(dataset) {
    return dataset.formula_records.filter(function (r) { return r.status === 'active'; })
      .map(function (r) { return { formula_id: r.formula_id, label: r.brand + ' – ' + r.name, reviewed_at: r.reviewed_at, adult_comparator: r.adult_comparator }; })
      .sort(function (a, b) { return a.label.localeCompare(b.label, 'en-GB'); });
  }

  function findFormula(dataset, id) {
    for (var i = 0; i < dataset.formula_records.length; i++) if (dataset.formula_records[i].formula_id === id) return dataset.formula_records[i];
    return null;
  }

  return {
    ENGINE_VERSION: ENGINE_VERSION,
    BENCHMARK_METRICS: BENCHMARK_METRICS,
    // inputs
    parseNumber: parseNumber, toMg: toMg, buildManualTarget: buildManualTarget, targetFromFormula: targetFromFormula,
    // calculations
    calculateLocal: calculateLocal, compare: compare, buildPool: buildPool, indexDataset: indexDataset,
    // statistics
    quantileR7: quantileR7, summarise: summarise, exactPosition: exactPosition, intervalPosition: intervalPosition,
    bandFor: bandFor, benchmarkStateFor: benchmarkStateFor,
    // misc
    coverageSummary: coverageSummary, checkSchema: checkSchema, catalogueOptions: catalogueOptions, findFormula: findFormula,
    formatGBP: formatGBP, formatMg: formatMg, near: near
  };
});
